import { AxiosError } from 'axios';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { IPasswordReset } from '../models/auth/resetPassword.model';
import { IsignInData, SignInDataType } from '../models/auth/signIn.model';
import { ISignupData } from '../models/auth/signUp.model';
import { ISigupPasswordChange } from '../models/auth/signUpPaswordChange.model';
import {
  ErrorHttpResponse,
  HttpConfig,
  SuccessHttpResponse,
} from '../models/https';
import { authActions } from '../redux/slices/authSlice';
import { useAppDisPatch } from '../redux/store';
import {
  ACCOUNT,
  HOME,
  SIGNUPCHANGEPASSWORD,
  SIGNUPCOMPLETE,
} from '../routes/routeConstants';
import useHttps from './useHttps';

const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const request = useHttps();
  const dispatch = useAppDisPatch();

  const onLoginError = (err: AxiosError<ErrorHttpResponse>) => {
    // console.log('onLoginError');
    toast.error(err?.response?.data?.message ?? 'Something went wrong');
  };

  const setAuthentication = ({ data }: SuccessHttpResponse<IsignInData>) => {
    dispatch(
      authActions.setAuthentication({ authData: data, isAuthenticated: true })
    );
    localStorage.setItem('authData', JSON.stringify(data));
    navigate(HOME, { replace: true });
  };

  const loginUser = (data: SignInDataType) => {
    const url: HttpConfig = {
      url: 'authentication-service/admin/login',
      method: 'post',
      data,
    };

    request(url, setAuthentication, setLoading, onLoginError);
  };

  const signupEmailPassword = ({ data }: SuccessHttpResponse<any>) => {
    navigate(`/${ACCOUNT}/${SIGNUPCHANGEPASSWORD}`, {
      replace: true,
      state: { data: data },
    });
  };

  const signupUser = (data: ISignupData) => {
    const url: HttpConfig = {
      url: 'authentication-service/admin/password/validate',
      method: 'put',
      data,
    };

    request(url, signupEmailPassword, setLoading, onLoginError);
  };

  const signupSetPaswd = () => {
    navigate(`/${ACCOUNT}/${SIGNUPCOMPLETE}`);
  };

  const signupSetUserPaswd = (data: ISigupPasswordChange) => {
    const url: HttpConfig = {
      url: 'authentication-service/admin/password/create',
      method: 'post',
      data,
    };

    request(url, signupSetPaswd, setLoading, onLoginError);
  };

  const logoutUser = () => {
    const onSuccess = () => {
      toast.success('Signed Out');
      localStorage.removeItem('authData');
      dispatch(authActions.reset());
      navigate('account/signin');
    };

    onSuccess();
  };

  const onRefreshScreen = () => {
    const data = localStorage.getItem('authData');

    if (data) {
      const authData = JSON.parse(data);
      if (!authData.user.verified) {
        navigate(`/account/${SIGNUPCHANGEPASSWORD}`, {
          replace: true,
          state: { data: authData.user.id },
        });
      }
      dispatch(
        authActions.setAuthentication({ authData, isAuthenticated: true })
      );
    } else {
      navigate('/account/signin', { replace: true });
    }
  };

  const onPasswordReset = ({ data }: SuccessHttpResponse<IsignInData>) => {
    navigate(`/${ACCOUNT}/${SIGNUPCHANGEPASSWORD}`);
  };

  const passwordReset = (data: IPasswordReset) => {
    const url: HttpConfig = {
      url: `authentication-service/admin/password/forget?email=${data.email}`,
      method: 'put',
      data,
    };

    request(url, onPasswordReset, setLoading, onLoginError);
  };

  return {
    loading,
    loginUser,
    signupUser,
    signupSetUserPaswd,
    logoutUser,
    onRefreshScreen,
    passwordReset,
  };
};

export default useAuth;
